// ---------------------------------------------------------------------------
// AuthoritiesStep — Checklist of authority/TSO/TAX tariffs
// ---------------------------------------------------------------------------
// Options carry authority-specific fields inside `metadata`:
//   - authority_type (tso|tax|authority|…)
//   - assignment (mandatory|default|conditional|optional)
//   - removable, direction (BOT|IMP|EXP), condition
//
// Backend expects:  { authorities: [{ authority_type, tariff_id, direction }, ...] }
// ---------------------------------------------------------------------------

import { memo, useCallback, useState } from 'react';
import { StepHeading } from '@/components/shared/StepHeading';
import { FooterActions } from '@/components/shell/FooterActions';
import { t } from '@/i18n';
import type { StepOption } from '@/api/types';
import { useCollectedData } from '@/hooks/useCollectedData';
import { getStepIcon } from '@/utils/stepIcons';
import type { StepProps } from '@/types/steps';
import styles from './AuthoritiesStep.module.css';
import { getRemovable } from '@/utils/stepOptions';

// The API omits `metadata` on options that have none (StepOptionSerializer, required=False).
function getAuthorityType(opt: StepOption): string {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  return (opt.metadata?.['authority_type'] as string | undefined) ?? opt.id;
}

function isCheckedByDefault(opt: StepOption): boolean {
  const assignment =
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    (opt.metadata?.['assignment'] as string | undefined) ?? opt.assignment ?? 'optional';
  return assignment === 'mandatory' || assignment === 'default' || assignment === 'conditional';
}

export const AuthoritiesStep = memo(function AuthoritiesStep({
  stepId,
  options,
  onSubmit,
  isSubmitting,
}: StepProps) {
  const collectedData = useCollectedData();

  const [selected, setSelected] = useState<Set<string>>(() => {
    // Pre-fill from collected_data.authorities (back-navigation).
    // New shape: list of { authority_type, tariff_id, direction }.
    const authData = collectedData['authorities'];
    if (Array.isArray(authData)) {
      const ids = new Set<string>();
      for (const entry of authData) {
        if (typeof entry === 'object' && entry !== null) {
          const tid = (entry as Record<string, unknown>)['tariff_id'];
          if (typeof tid === 'string') ids.add(tid);
        }
      }
      if (ids.size > 0) return ids;
    }
    // Fall back to assignment-based defaults
    const initial = new Set<string>();
    for (const opt of options.options) {
      if (isCheckedByDefault(opt)) initial.add(opt.id);
    }
    return initial;
  });

  const handleToggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleSubmit = useCallback(() => {
    // Build the list-based authorities payload:
    //   { authorities: [{ authority_type, tariff_id, direction }, ...] }
    // The list shape preserves multiple mandatory tariffs per authority_type
    // (German LCTs commonly have several charges sharing one type).
    const authorities: Array<{
      authority_type: string;
      tariff_id: string;
      direction: string;
    }> = [];
    for (const tariffId of selected) {
      const opt = options.options.find((o) => o.id === tariffId);
      if (opt) {
        authorities.push({
          authority_type: getAuthorityType(opt),
          tariff_id: tariffId,
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          direction: (opt.metadata?.['direction'] as string | undefined) ?? 'BOT',
        });
      }
    }
    onSubmit({ authorities });
  }, [onSubmit, selected, options.options]);

  return (
    <>
      <StepHeading
        title={t('steps.authorities.title')}
        subtitle={t('steps.authorities.subtitle')}
        icon={getStepIcon(stepId)}
      />

      <fieldset className={styles['list']} disabled={isSubmitting}>
        <legend className={styles['sr-only']}>{t('steps.authorities.legend')}</legend>
        {options.options.map((opt) => {
          const checked = selected.has(opt.id);
          const removable = getRemovable(opt);

          return (
            <label
              key={opt.id}
              className={`${styles['row']} ${!removable ? styles['disabled'] : ''}`}
            >
              <input
                type="checkbox"
                className={styles['checkbox']}
                checked={checked}
                disabled={!removable}
                onChange={() => handleToggle(opt.id)}
              />
              <span className={styles['content']}>
                <span className={styles['name']}>
                  {opt.name}
                </span>
                {opt.description && (
                  <span className={styles['meta']}>{opt.description}</span>
                )}
              </span>
            </label>
          );
        })}
      </fieldset>

      <FooterActions
        primaryLabel={t('common.next')}
        onPrimary={handleSubmit}
        isLoading={isSubmitting}
      />
    </>
  );
});
